import {
    translateParts,
    translatePartsOptional,
} from 'aos-helpers/src/helpers/translations/Translations'
import { uuid } from 'aos-helpers/src/helpers/Uuid'
import {
    RescueServiceTaskFormPayload,
    RescueServiceUnitPayload,
} from 'aos-services/src/services/tasks/types/payload/RescueServiceTaskFormPayload'
import { FireTruck } from 'aos-services/src/services/tasks/types/task/FireTruck'
import { RescueServiceTaskType } from 'aos-services/src/services/tasks/types/task/RescueServiceTask'
import { TaskMetadata } from 'aos-services/src/services/tasks/types/TaskMetadata'
import { Box, Dropdown, HStack, Input, InputField } from 'aos-ui-universal'
import { isNull } from 'lodash'
import React, { FC } from 'react'
import { Controller, useController, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { TaskFormField } from '../TaskFormField'
import { TaskEditableListField } from './partials/TaskEditableListField'
import { TaskTextField } from './partials/TaskTextField'

interface RescueServiceTaskFormProps {
    metadata: TaskMetadata
    isTaskTemplate: boolean
}

export const RescueServiceTaskForm: FC<RescueServiceTaskFormProps> = ({
    metadata,
    isTaskTemplate,
}) => {
    const { t: translate } = useTranslation()
    const {
        control,
        formState: { errors },
    } = useFormContext<RescueServiceTaskFormPayload>()
    const titleField = useController({ control, name: 'title' })
    return (
        <>
            <Controller
                control={control}
                name='taskType'
                render={({ field, fieldState }) => (
                    <TaskFormField
                        className='mb-3'
                        label={translateParts(translate)(
                            'tasks.form.rescue-service.task-type',
                            'label',
                        )}
                        required={!isTaskTemplate}
                        error={
                            fieldState.error
                                ? translatePartsOptional(translate)(
                                      'tasks.form.rescue-service.task-type',
                                      'error',
                                  )
                                : undefined
                        }
                        seleniumLocation='task-type'
                    >
                        <Dropdown<RescueServiceTaskType>
                            items={metadata.rescueServiceTaskTypes}
                            value={field.value ?? null}
                            labelRenderer={(l: RescueServiceTaskType) => l.label}
                            placeholder={translatePartsOptional(translate)(
                                'tasks.form.rescue-service.task-type',
                                'placeholder',
                            )}
                            isInvalid={!!fieldState.error}
                            onChange={taskType => {
                                field.onChange(taskType ?? undefined)
                                titleField.field.onChange(taskType?.label)
                            }}
                        />
                    </TaskFormField>
                )}
            />
            <Controller
                control={control}
                name='address'
                render={({ field }) => (
                    <TaskTextField
                        className='mb-3'
                        keyPrefix='tasks.form.rescue-service.address'
                        value={field.value}
                        onChangeText={field.onChange}
                    />
                )}
            />
            <Controller
                control={control}
                name='aircraftRegistration'
                render={({ field }) => (
                    <TaskTextField
                        className='mb-3'
                        keyPrefix='tasks.form.rescue-service.aircraft-registration'
                        value={field.value}
                        onChangeText={field.onChange}
                    />
                )}
            />
            <Controller
                control={control}
                name='units'
                render={({ field }) => {
                    const unitsErrors = errors.units
                    return (
                        <TaskEditableListField
                            keyPrefix='tasks.form.rescue-service.unit'
                            value={field.value}
                            emptyItem={{ uuid: uuid() }}
                            onChange={field.onChange}
                            validation={(field.value || []).map((_, index) => ({
                                isError:
                                    !!unitsErrors?.[index]?.name ||
                                    !!unitsErrors?.[index]?.numberOfWorkers,
                                errorMessage: translate('form.rescue-service.unit-entry.error', {
                                    ns: 'tasks',
                                }),
                            }))}
                            itemRenderer={({ item, index, onChangeItem }) => (
                                <RescueServiceUnitRenderer
                                    allFireTrucks={metadata.fireTrucks}
                                    unit={item}
                                    key={index}
                                    onChange={onChangeItem}
                                />
                            )}
                        />
                    )
                }}
            />
            <Controller
                control={control}
                name='additionalUnits'
                render={({ field }) => (
                    <TaskTextField
                        className='mb-3'
                        keyPrefix='tasks.form.rescue-service.additional-unit'
                        value={field.value}
                        onChangeText={field.onChange}
                    />
                )}
            />
        </>
    )
}

interface RescueServiceUnitRendererProps {
    unit: RescueServiceUnitPayload
    allFireTrucks: FireTruck[]
    onChange(v: RescueServiceUnitPayload): void
}

export const RescueServiceUnitRenderer: FC<RescueServiceUnitRendererProps> = ({
    unit,
    allFireTrucks,
    onChange,
}) => {
    const { t: translate } = useTranslation()
    return (
        <HStack className='mb-3 items-start gap-5 pr-4'>
            <Box className='flex-[2]'>
                <Dropdown<string>
                    items={allFireTrucks.map(fireTruck => fireTruck.callSign)}
                    value={unit.name ?? null}
                    labelRenderer={name => name}
                    placeholder={translate('form.rescue-service.unit-name.placeholder', {
                        ns: 'tasks',
                    })}
                    onChange={name => onChange({ ...unit, name: isNull(name) ? undefined : name })}
                />
            </Box>
            <Box className='flex-1'>
                <Input>
                    <InputField
                        value={
                            unit.numberOfWorkers === undefined ? '' : String(unit.numberOfWorkers)
                        }
                        placeholder={translate('form.rescue-service.unit-number.placeholder', {
                            ns: 'tasks',
                        })}
                        keyboardType='numeric'
                        onChangeText={next => {
                            const digits = next.replace(/\D/g, '')
                            onChange({
                                ...unit,
                                numberOfWorkers: digits ? Number(digits) : undefined,
                            })
                        }}
                    />
                </Input>
            </Box>
        </HStack>
    )
}
